import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export interface Column<T> {
  header: string;
  accessorKey?: keyof T;
  cell?: (item: T, index: number) => React.ReactNode;
  headerClassName?: string;
}

interface TableComponentProps<T> {
  data: T[];
  columns: Column<T>[];
  pageSize?: number;
  className?: string;
  totalPages: number;
  showRowNumbers?: boolean;
  onPageChange: (page: number) => void;
  currentPage: number;
  isPagination?: boolean;
}

export function TableComponent<T>({
  data,
  columns,
  pageSize = 10,
  totalPages,
  className,
  showRowNumbers = false,
  onPageChange,
  currentPage,
  isPagination = true,
}: TableComponentProps<T>) {
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = data;

  const handlePageChange = (page: number) => {
    onPageChange(page);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    // Always show first page
    items.push(
      <PaginationItem key="first">
        <PaginationLink
          onClick={() => handlePageChange(1)}
          isActive={currentPage === 1}
          className={
            currentPage === 1
              ? 'bg-luxury-100 border-luxury-200 text-luxury-900'
              : 'hover:bg-luxury-50 hover:text-luxury-900'
          }
        >
          1
        </PaginationLink>
      </PaginationItem>
    );

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 2; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
              className={
                currentPage === i
                  ? 'bg-luxury-100 border-luxury-200 text-luxury-900'
                  : 'hover:bg-luxury-50 hover:text-luxury-900'
              }
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      // Show ellipsis and pages around current page
      if (currentPage > 3) {
        items.push(
          <PaginationItem key="start-ellipsis">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
              className={
                currentPage === i
                  ? 'bg-luxury-100 border-luxury-200 text-luxury-900'
                  : 'hover:bg-luxury-50 hover:text-luxury-900'
              }
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (currentPage < totalPages - 2) {
        items.push(
          <PaginationItem key="end-ellipsis">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      // Always show last page
      items.push(
        <PaginationItem key="last">
          <PaginationLink
            onClick={() => handlePageChange(totalPages)}
            isActive={currentPage === totalPages}
            className={
              currentPage === totalPages
                ? 'bg-luxury-100 border-luxury-200 text-luxury-900'
                : 'hover:bg-luxury-50 hover:text-luxury-900'
            }
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <div className={`w-full ${className || ''}`}>
      <div className="rounded-md border border-luxury-200 shadow-sm bg-white/80 backdrop-blur-sm overflow-x-auto max-h-[calc(100vh-250px)]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-luxury-200 bg-luxury-50/70">
              {showRowNumbers && (
                <TableHead className="h-12 px-4 text-left align-middle font-medium text-luxury-900 w-[80px] sticky left-0 bg-luxury-50 z-20">
                  STT
                </TableHead>
              )}
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={`h-12 px-4 text-left align-middle font-medium text-luxury-900 ${
                    index === columns.length - 1
                      ? 'sticky right-0 bg-luxury-50 z-20'
                      : index === 0
                      ? `sticky ${
                          showRowNumbers ? 'left-[80px]' : 'left-0'
                        } bg-luxury-50 z-20`
                      : ''
                  } ${column.headerClassName}`}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showRowNumbers ? columns.length + 1 : columns.length}
                  className="h-24 text-center text-luxury-700"
                >
                  Không có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              currentData.map((item, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className="hover:bg-luxury-50/70 transition-colors border-b border-luxury-100"
                >
                  {showRowNumbers && (
                    <TableCell className="p-4 align-baseline sticky left-0 bg-white z-20">
                      {startIndex + rowIndex + 1}
                    </TableCell>
                  )}
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={colIndex}
                      className={`p-4 align-baseline ${
                        colIndex === columns.length - 1
                          ? 'sticky right-0 bg-white z-20'
                          : colIndex === 0
                          ? `sticky ${
                              showRowNumbers ? 'left-[80px]' : 'left-0'
                            } bg-white z-20`
                          : ''
                      }`}
                    >
                      {column.cell
                        ? column.cell(item, startIndex + rowIndex)
                        : (item[column.accessorKey!] as React.ReactNode) || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {isPagination && (
        <Pagination className="mt-6">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() =>
                  currentPage === 1 || totalPages <= 1
                    ? null
                    : handlePageChange(currentPage - 1)
                }
                className={
                  currentPage === 1 || totalPages <= 1
                    ? 'pointer-events-none opacity-50'
                    : 'hover:bg-luxury-50 hover:text-luxury-900 border-luxury-200'
                }
              />
            </PaginationItem>
            {renderPaginationItems()}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  currentPage === totalPages || totalPages <= 1
                    ? null
                    : handlePageChange(currentPage + 1)
                }
                className={
                  currentPage === totalPages || totalPages <= 1
                    ? 'pointer-events-none opacity-50'
                    : 'hover:bg-luxury-50 hover:text-luxury-900 border-luxury-200'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
